import _ from 'lodash';
import createModel, { prop } from './lib/create_model';

import { NotificationSoundType } from 'actions/notification/lib/notification_constants';

// Mirrors the backend agentpreferences.ThemeMode enum. An empty/absent value is
// treated as light; the backend omits themeMode from the payload when unset, so
// a record predating the field never carries it (see the gateway observer).
export const ThemeMode = Object.freeze({
  LIGHT: 'LIGHT',
  DARK: 'DARK',
  AUTO: 'AUTO',
});

const AgentPreferences = createModel({
  modelName: 'AgentPreferences',

  properties: {
    // Preferences the agent explicitly sets.
    agentNotificationSound: prop(String).default(NotificationSoundType.AGENT_NOTIFICATION),
    answerSearchAudiences: prop([String]).default([]),
    answerSearchLanguage: prop(String).default(''),
    channelColors: prop(Object).default({}),
    channelNotificationSounds: prop(Object).default({}),
    conversationSummaryControlsEnabled: prop(Boolean).default(false),
    customizeChannelColors: prop(Boolean).default(false),
    customizeChannelNotificationSounds: prop(Boolean).default(false),
    detailedDatesEnabled: prop(Boolean).default(false),
    experimentalEditorEnabled: prop(Boolean).default(false),
    pinnedNavItemIds: prop([String]).default([]),
    // Agent's dragged width for the customer side panel (Answers / Team Assist /
    // Task), in CSS pixels. 0 means the agent has never dragged it, in which case
    // the default width applies. Stored unclamped: the bounds depend on the
    // window the agent is currently on, and this value syncs across devices, so
    // clamping happens at render (see components/customer/lib/side_panel_width).
    sidePanelWidth: prop(Number).default(0),
    themeMode: prop(String).default(''),
    // Agent's chosen UI language (BCP-47 base). Empty when the agent has never
    // picked one, in which case detection/defaults apply. Deliberately not an
    // `oneOf` enum (like themeMode above): oneOf violations throw during model
    // construction in dev builds, so an unrecognized value from the backend — a
    // newer build's language, or a region-suffixed tag — would abort the whole of
    // the gateway observer's onFetchSuccess. The supported set is enforced at the
    // single choke point instead: resolveSupportedLanguage in scripts/i18n/i18n.js.
    uiLanguage: prop(String).default(''),

    // Persisted state — not preferences, but data we want backed up across devices
    // (e.g. so the agent doesn't re-see a notification or feature tip elsewhere).
    agentNotificationsLastOpenedAt: prop(String),
    readFeatureNotifications: prop(Object).default({}),
    seenFeatureTips: prop(Object).default({}),
  },

  // Mirror the backend's `omitempty` fields: don't send an empty default so an
  // agent who never set the value PUTs no field at all rather than an empty
  // string on every unrelated prefs change (and so the observer can distinguish
  // "unset" from an explicit value on fetch).
  overrideToJs(originalToJs) {
    return function () {
      const json = originalToJs();
      if (json.themeMode === '') {
        delete json.themeMode;
      }
      if (json.uiLanguage === '') {
        delete json.uiLanguage;
      }
      if (!json.sidePanelWidth) {
        delete json.sidePanelWidth;
      }
      return json;
    };
  },

  setAgentNotificationSound(sound) {
    this.agentNotificationSound = sound;
  },

  setAnswerSearchAudiences(audiences) {
    this.answerSearchAudiences = audiences;
  },

  setAnswerSearchLanguage(language) {
    this.answerSearchLanguage = language;
  },

  setChannelNotificationSound(channel, sound) {
    this.channelNotificationSounds = { ...this.channelNotificationSounds, [channel]: sound };
  },

  setCustomizeChannelNotificationSounds(enabled) {
    this.customizeChannelNotificationSounds = enabled;
  },

  setChannelColor(channel, color) {
    this.channelColors = { ...this.channelColors, [channel]: color };
  },

  setCustomizeChannelColors(enabled) {
    this.customizeChannelColors = enabled;
  },

  toggleConversationSummaryControls() {
    this.conversationSummaryControlsEnabled = !this.conversationSummaryControlsEnabled;
  },

  toggleDetailedDates() {
    this.detailedDatesEnabled = !this.detailedDatesEnabled;
  },

  setThemeMode(themeMode) {
    this.themeMode = themeMode;
  },

  setUiLanguage(uiLanguage) {
    this.uiLanguage = uiLanguage;
  },

  setSidePanelWidth(width) {
    this.sidePanelWidth = width;
  },

  toggleExperimentalEditor() {
    this.experimentalEditorEnabled = !this.experimentalEditorEnabled;
  },

  isNavItemPinned(id) {
    return this.pinnedNavItemIds.includes(id);
  },

  toggleNavItemPin(id) {
    if (this.isNavItemPinned(id)) {
      this.pinnedNavItemIds = _.filter(this.pinnedNavItemIds, pinId => pinId !== id);
    } else {
      this.pinnedNavItemIds.push(id);
    }
  },

  setAgentNotificationsLastOpenedAt(lastOpenedAt) {
    this.agentNotificationsLastOpenedAt = lastOpenedAt;
  },

  hasSeenFeatureNotification(id) {
    return !!this.readFeatureNotifications[id];
  },

  markFeatureAsSeen(id) {
    this.readFeatureNotifications[id] = true;
  },

  hasSeenFeatureTip(feature) {
    return !!this.seenFeatureTips[feature];
  },

  markFeatureTipAsSeen(feature) {
    this.seenFeatureTips[feature] = true;
  },
});

export default AgentPreferences;
